// deno-lint-ignore-file no-unused-vars
// Panel załączników WEWNĄTRZ karty zadania. Otwierany ikoną 📎 w meta row (lub Ctrl+V obrazu).
// Opis NIE jest tu — rozwija się i edytuje w miejscu w wierszu (tmk-task-item.tsx).
// Spec: spec/lista-zadan.md → "Ikony 📝 📎 🔁 i panel załączników".
import { h, Fragment } from '../jsx-runtime.ts';
import type { TaskWithTags } from '../types.ts';
import { AttachmentsAPI, type Attachment } from '../api.ts';
import { showToast } from './tmk-toast.tsx';

/** 'attachments' = ustaw fokus na „+ Dodaj plik” i przewiń do panelu; null = tylko pokaż. */
export type ExtrasFocus = 'attachments' | null;

export interface TaskExtrasCallbacks {
  onClose: () => void;
  /** Liczba załączników zmieniona — wiersz (licznik 📎) trzeba odświeżyć. */
  onChanged: () => Promise<void>;
}

function formatSize(b: number): string {
  return b < 1024 ? b + ' B' : b < 1048576 ? (b / 1024).toFixed(1) + ' KB' : (b / 1048576).toFixed(1) + ' MB';
}

export function renderTaskExtras(task: TaskWithTags, focus: ExtrasFocus, cb: TaskExtrasCallbacks): HTMLElement {
  const panel = <div className="task-extras" role="region" aria-label="Załączniki zadania" /> as HTMLElement;

  // Zdarzenia nie mogą wyciekać do wiersza (long-press zaznaczania, D&D, skróty na .task-item).
  for (const type of ['click', 'mousedown', 'mouseup', 'dblclick']) {
    panel.addEventListener(type, (e: Event) => e.stopPropagation());
  }
  panel.addEventListener('touchstart', (e: Event) => e.stopPropagation(), { passive: true });

  const section = <div className="task-extras-section" /> as HTMLElement;
  const head = <div className="task-extras-label" /> as HTMLElement;
  head.appendChild(<span>Załączniki</span> as HTMLElement);
  const headActions = <span className="task-extras-head-actions" /> as HTMLElement;
  const fileInput = <input type="file" style={{ display: 'none' }} /> as HTMLInputElement;
  const uploadBtn = <label className="btn btn-ghost btn-sm detail-upload-btn">+ Dodaj plik</label> as HTMLElement;
  uploadBtn.appendChild(fileInput);
  headActions.appendChild(uploadBtn);
  headActions.appendChild(<button type="button" className="task-extras-close" title="Zwiń załączniki (Escape)"
    onClick={() => cb.onClose()}>✕</button> as HTMLElement);
  head.appendChild(headActions);
  section.appendChild(head);
  const list = <div className="task-extras-attachments" /> as HTMLElement;
  section.appendChild(list);
  section.appendChild(<div className="task-extras-hint">Obraz ze schowka: kliknij zadanie i wciśnij Ctrl+V — trafi tu jako załącznik.</div> as HTMLElement);
  panel.appendChild(section);

  let attachments: Attachment[] = [];
  const renderAttachments = () => {
    list.textContent = '';
    if (attachments.length === 0) {
      list.appendChild(<span className="task-extras-hint">Brak załączników</span> as HTMLElement);
      return;
    }
    for (const att of attachments) {
      const row = <div className="detail-attachment-row" /> as HTMLElement;
      row.appendChild(<a href={AttachmentsAPI.fileUrl(att.filename)} target="_blank" rel="noopener" className="detail-attachment-name">{att.original_name}</a> as HTMLElement);
      row.appendChild(<span className="detail-attachment-size">{formatSize(att.size_bytes)}</span> as HTMLElement);
      row.appendChild(<button type="button" className="detail-attachment-delete" title="Usuń załącznik" onClick={async () => {
        try {
          await AttachmentsAPI.delete(att.id);
          attachments = attachments.filter(a => a.id !== att.id);
          task.attachments_count = attachments.length;
          renderAttachments();
          await cb.onChanged();
        } catch (err: unknown) {
          showToast('Błąd usuwania: ' + (err as Error).message, 'error');
        }
      }}>✕</button> as HTMLElement);
      list.appendChild(row);
    }
  };
  list.appendChild(<span className="task-extras-hint">Ładowanie…</span> as HTMLElement);
  AttachmentsAPI.list(task.id)
    .then((a) => { attachments = a; renderAttachments(); })
    .catch((err: unknown) => { list.textContent = 'Nie udało się pobrać załączników: ' + (err as Error).message; });

  fileInput.addEventListener('change', async () => {
    const file = fileInput.files?.[0];
    if (!file) return;
    fileInput.value = '';
    try {
      const att = await AttachmentsAPI.upload(task.id, file);
      attachments.unshift(att);
      task.attachments_count = attachments.length;
      renderAttachments();
      showToast('Załącznik dodany', 'success');
      await cb.onChanged();
    } catch (err: unknown) {
      showToast('Błąd uploadu: ' + (err as Error).message, 'error');
    }
  });

  // Escape zwija panel.
  panel.addEventListener('keydown', (e: Event) => {
    if ((e as KeyboardEvent).key === 'Escape') { e.stopPropagation(); cb.onClose(); }
  });

  requestAnimationFrame(() => {
    if (focus === 'attachments') {
      (uploadBtn as HTMLElement).focus?.();
      panel.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  });

  return panel;
}
